import React, { useCallback, useMemo, useState } from 'react';

export type KakaoButtonType = 'WL' | 'AL' | 'DS' | 'BK' | 'MD' | 'AC';

export interface KakaoButton {
  name: string; // label shown
  type: KakaoButtonType;
  url_mobile?: string;
  url_pc?: string;
  scheme_android?: string;
  scheme_ios?: string;
}

interface ButtonsEditorProps {
  value?: KakaoButton[];
  onChange?: (buttons: KakaoButton[]) => void;
  maxButtons?: number; // Kakao limit typically 5
  disabled?: boolean;
  channel: 'AT' | 'FT'; // AlimTalk / FriendTalk
  externalError?: string | null; // validation schema level error
}

const DEFAULT_BUTTON: KakaoButton = { name: '', type: 'WL', url_mobile: '' };

const buttonTypeLabels: Record<KakaoButtonType, string> = {
  WL: '웹링크',
  AL: '앱링크',
  DS: '배송조회',
  BK: '봇키워드',
  MD: '메시지전달',
  AC: '채널추가',
};

function validateButton(b: KakaoButton): string | null {
  if (!b.name.trim()) return '버튼 이름 필요';
  if (b.name.length > 14) return '버튼 이름 14자 이내';
  if (b.type === 'WL') {
    if (!b.url_mobile?.trim()) return '모바일 URL 필요';
    if (!/^https?:\/\//i.test(b.url_mobile)) return '모바일 URL 형식 오류';
    if (b.url_pc && !/^https?:\/\//i.test(b.url_pc)) return 'PC URL 형식 오류';
  }
  if (b.type === 'AL') {
    if (!b.scheme_android && !b.scheme_ios) return '앱 스킴 중 하나 필요';
  }
  return null;
}

export const ButtonsEditor: React.FC<ButtonsEditorProps> = ({ value = [], onChange, maxButtons = 5, disabled, channel, externalError }) => {
  const [local, setLocal] = useState<KakaoButton[]>(value);
  const [touched, setTouched] = useState<Record<number, boolean>>({});

  const update = (buttons: KakaoButton[]) => {
    setLocal(buttons);
    onChange && onChange(buttons);
  };

  const addButton = () => {
    if (disabled) return;
    if (local.length >= maxButtons) return;
    update([...local, { ...DEFAULT_BUTTON }]);
  };

  const removeButton = (idx: number) => {
    if (disabled) return;
    update(local.filter((_, i) => i !== idx));
  };

  const move = (idx: number, dir: -1 | 1) => {
    if (disabled) return;
    const target = idx + dir;
    if (target < 0 || target >= local.length) return;
    const copy = [...local];
    const temp = copy[idx];
    copy[idx] = copy[target];
    copy[target] = temp;
    update(copy);
  };

  const setField = (idx: number, field: keyof KakaoButton, value: string) => {
    const copy = [...local];
    (copy[idx] as any)[field] = value;
    update(copy);
    setTouched(t => ({ ...t, [idx]: true }));
  };

  const errors = useMemo(() => local.map(validateButton), [local]);
  const hasErrors = errors.some(e => e != null);

  const channelInfo = channel === 'AT' ? '알림톡: 템플릿 허용된 버튼만 사용' : '친구톡: 최대 5개 버튼';

  return (
    <div className="buttons-editor" style={{ border: '1px solid #ddd', padding: 12, borderRadius: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>버튼 ({local.length}/{maxButtons})</strong>
        <div style={{ fontSize: 12, color: '#666' }}>{channelInfo}</div>
      </div>
      {local.map((b, idx) => {
        const error = touched[idx] ? errors[idx] : null;
        return (
          <div key={idx} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8, background: '#fafafa', padding: 8, borderRadius: 4 }}>
            <input
              type="text"
              placeholder="버튼명"
              value={b.name}
              maxLength={14}
              disabled={disabled}
              onChange={e => setField(idx, 'name', e.target.value)}
              style={{ flex: '1 1 120px' }}
            />
            <select
              value={b.type}
              disabled={disabled}
              onChange={e => setField(idx, 'type', e.target.value as KakaoButtonType)}
            >
              {Object.entries(buttonTypeLabels).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            {b.type === 'WL' && (
              <>
                <input
                  type="text"
                  placeholder="모바일 URL"
                  value={b.url_mobile || ''}
                  disabled={disabled}
                  onChange={e => setField(idx, 'url_mobile', e.target.value)}
                  style={{ flex: '2 1 220px' }}
                />
                <input
                  type="text"
                  placeholder="PC URL (선택)"
                  value={b.url_pc || ''}
                  disabled={disabled}
                  onChange={e => setField(idx, 'url_pc', e.target.value)}
                  style={{ flex: '2 1 220px' }}
                />
              </>
            )}
            {b.type === 'AL' && (
              <>
                <input
                  type="text"
                  placeholder="Android 스킴"
                  value={b.scheme_android || ''}
                  disabled={disabled}
                  onChange={e => setField(idx, 'scheme_android', e.target.value)}
                  style={{ flex: '1 1 160px' }}
                />
                <input
                  type="text"
                  placeholder="iOS 스킴"
                  value={b.scheme_ios || ''}
                  disabled={disabled}
                  onChange={e => setField(idx, 'scheme_ios', e.target.value)}
                  style={{ flex: '1 1 160px' }}
                />
              </>
            )}
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button type="button" disabled={disabled || idx === 0} onClick={() => move(idx, -1)}>▲</button>
              <button type="button" disabled={disabled || idx === local.length - 1} onClick={() => move(idx, 1)}>▼</button>
              <button type="button" disabled={disabled} onClick={() => removeButton(idx)}>삭제</button>
            </div>
            {error && <div style={{ width: '100%', fontSize: 12, color: 'red' }}>{error}</div>}
          </div>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button type="button" disabled={disabled || local.length >= maxButtons} onClick={addButton}>버튼 추가</button>
        {(hasErrors || externalError) && <div style={{ fontSize: 12, color: 'red' }}>{externalError || '버튼 오류를 수정하세요.'}</div>}
      </div>
    </div>
  );
};

export default ButtonsEditor;
